import React, { Component } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Badge,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';
import classnames from 'classnames';
import RecentAlertsPanel from './RecentAlertsPanel';
import EmailLogsPanel from './EmailLogsPanel';
import EmailConfigPanel from './EmailConfigPanel';
import AuthService from '../../Auth/AuthService';
import PermissionFeature from '../../Permissions/PermissionFeature';

/**
 * AlertActivityPanel Component
 *
 * Combines Recent Alerts, Email Logs, and Email Configuration into a single tabbed interface
 * Provides a unified view of all alert, email activity, and email settings
 */
class AlertActivityPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            activeTab: '1', // '1' = Recent Alerts, '2' = Email Logs, '3' = Email Configuration
            alertCount: 0,
            emailCount: 0,
            hoursToShow: props.hours || 24 // Default to 24 hours or props value
        };
    }

    toggleTab = (tab) => {
        if (this.state.activeTab !== tab) {
            this.setState({ activeTab: tab });
        }
    };

    handleAlertCountUpdate = (count) => {
        this.setState({ alertCount: count });
    };

    handleEmailCountUpdate = (count) => {
        this.setState({ emailCount: count });
    };

    handleTimeRangeChange = (hours) => {
        this.setState({ hoursToShow: hours });
    };

    getTimeRangeLabel = (hours) => {
        if (hours === 24) return '24 hours';
        if (hours === 168) return '7 days';
        if (hours === 720) return '30 days';
        return `${hours} hours`;
    };

    render() {
        const { activeTab, alertCount, emailCount, hoursToShow } = this.state;

        // Check permissions
        const canViewAlerts = AuthService.canView(PermissionFeature.MonitoringDashboardViewAlerts);
        const canViewEmailLogs = AuthService.canView(PermissionFeature.MonitoringDashboardViewEmailLogs);

        // Set default active tab based on permissions
        let currentTab = activeTab;
        if (currentTab === '1' && !canViewAlerts && canViewEmailLogs) {
            currentTab = '2'; // Switch to Email Logs if Recent Alerts is not accessible
        } else if (currentTab === '2' && !canViewEmailLogs && canViewAlerts) {
            currentTab = '1'; // Switch to Recent Alerts if Email Logs is not accessible
        } else if ((currentTab === '1' && !canViewAlerts) || (currentTab === '2' && !canViewEmailLogs)) {
            // If current tab is not accessible and user has neither permission, default to Email Configuration
            if (!canViewAlerts && !canViewEmailLogs) {
                currentTab = '3';
            }
        }

        return (
            <Card className="mb-3">
                <CardHeader className="bg-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="fa fa-bell mr-2"></i>
                            Alert & Email Activity
                        </h5>
                        <UncontrolledDropdown>
                            <DropdownToggle caret color="light" size="sm">
                                <i className="fa fa-clock mr-1"></i>
                                Last {this.getTimeRangeLabel(hoursToShow)}
                            </DropdownToggle>
                            <DropdownMenu right>
                                <DropdownItem
                                    active={hoursToShow === 24}
                                    onClick={() => this.handleTimeRangeChange(24)}
                                >
                                    Last 24 hours
                                </DropdownItem>
                                <DropdownItem
                                    active={hoursToShow === 168}
                                    onClick={() => this.handleTimeRangeChange(168)}
                                >
                                    Last 7 days
                                </DropdownItem>
                                <DropdownItem
                                    active={hoursToShow === 720}
                                    onClick={() => this.handleTimeRangeChange(720)}
                                >
                                    Last 30 days
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </div>

                    <Nav tabs className="mt-3" style={{ borderBottom: 'none' }}>
                        {canViewAlerts && (
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: currentTab === '1' })}
                                    onClick={() => this.toggleTab('1')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <i className="fa fa-exclamation-triangle mr-2"></i>
                                    Recent Alerts
                                    {alertCount > 0 && (
                                        <Badge color="danger" pill className="ml-2">
                                            {alertCount}
                                        </Badge>
                                    )}
                                </NavLink>
                            </NavItem>
                        )}
                        {canViewEmailLogs && (
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: currentTab === '2' })}
                                    onClick={() => this.toggleTab('2')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <i className="fa fa-envelope mr-2"></i>
                                    Email Logs
                                    {emailCount > 0 && (
                                        <Badge color="info" pill className="ml-2">
                                            {emailCount}
                                        </Badge>
                                    )}
                                </NavLink>
                            </NavItem>
                        )}
                        <NavItem>
                            <NavLink
                                className={classnames({ active: currentTab === '3' })}
                                onClick={() => this.toggleTab('3')}
                                style={{ cursor: 'pointer' }}
                            >
                                <i className="fa fa-cog mr-2"></i>
                                Email Configuration
                            </NavLink>
                        </NavItem>
                    </Nav>
                </CardHeader>

                <CardBody className="p-0">
                    <TabContent activeTab={currentTab}>
                        {canViewAlerts && (
                            <TabPane tabId="1">
                                {currentTab === '1' && (
                                    <RecentAlertsPanel
                                        hours={hoursToShow}
                                        standalone={false}
                                        onCountUpdate={this.handleAlertCountUpdate}
                                    />
                                )}
                            </TabPane>
                        )}
                        {canViewEmailLogs && (
                            <TabPane tabId="2">
                                {currentTab === '2' && (
                                    <EmailLogsPanel
                                        hours={hoursToShow}
                                        standalone={false}
                                        onCountUpdate={this.handleEmailCountUpdate}
                                    />
                                )}
                            </TabPane>
                        )}
                        <TabPane tabId="3">
                            {currentTab === '3' && (
                                <div className="p-3">
                                    <EmailConfigPanel />
                                </div>
                            )}
                        </TabPane>
                    </TabContent>
                </CardBody>
            </Card>
        );
    }
}

export default AlertActivityPanel;
